import { ResponseError } from '@backstage/errors';
import {
  ApiRef,
  DiscoveryApi,
  FetchApi,
  createApiRef,
} from '@backstage/frontend-plugin-api';

export type PolicyRef = { id: string; revision: string };

export type QuotaQuantity = {
  state: 'known' | 'unknown' | 'notApplicable';
  value?: string;
  reason?: string;
};

export type QuotaBudget = {
  budgetId: string;
  policyRef: PolicyRef;
  bindingRef: PolicyRef;
  quotaId: string;
  scope: {
    level: 'tenant' | 'applicationGroup';
    tenant: string;
    applicationGroupId?: string;
  };
  kinds: string[];
  stages: string[];
  operations: string[];
  mode: 'audit' | 'deny';
  enforcementState: string;
  dimension: string;
  unit: string;
  limit: string;
  allocated: QuotaQuantity;
  reserved: QuotaQuantity;
  remaining: QuotaQuantity;
  overage: QuotaQuantity;
  inventory: {
    revision: string;
    capturedAt: string;
    freshUntil: string;
    complete: boolean;
    unresolved: string[];
  };
  ledgerRevision: string;
  schemaRevision: string;
};

export type QuotaPage = {
  items: QuotaBudget[];
  snapshotRevision: string;
  evaluatedAt: string;
  has_more: boolean;
  next_cursor?: string;
};

export type QuotaRefreshRequest = {
  queued: number;
  maxPerRequest: number;
  message: string;
};

export type PolicyAdministrationTenants = {
  tenants: string[];
  platformAdmin: boolean;
};

export type PolicyLibraryEntry = {
  ref: PolicyRef;
  tenant?: string;
  scope?: { level: string; tenant?: string; applicationGroupId?: string };
  targetKinds: string[];
  rules: unknown[];
  quotas: unknown[];
};

export type PolicyLibraryPage = {
  policies: PolicyLibraryEntry[];
  has_more: boolean;
  next_cursor?: string;
};

export type PlatformPolicySummaryPage = {
  tenants: Array<{
    tenant: string;
    state: 'observed' | 'unavailable';
    activeGroups?: string;
    freshSweptGroups?: string;
    currentEvaluations?: string;
    violatingResources?: string;
    unknownEvaluations?: string;
    latestEvaluationAt?: string;
  }>;
  evaluatedAt: string;
  has_more: boolean;
  next_cursor?: string;
};

export type EffectiveBinding = {
  ref: PolicyRef;
  policyRef: PolicyRef;
  mode: 'audit' | 'deny';
  scope: {
    level: string;
    tenant?: string;
    applicationGroupId?: string;
  };
  kinds: string[];
  stages: string[];
  operations: string[];
};

export type ComplianceRecord = {
  id: string;
  environment: string;
  applicationGroupId: string;
  status: string;
  decision: string;
  evaluatedAt: string;
  observedAt?: string;
  freshUntil?: string;
  coverage: { complete: boolean; unknownFields: string[] };
  results: Array<{
    ruleId: string;
    outcome: string;
    message?: string;
    reason?: string;
    mode: string;
  }>;
};

export type CompliancePage = {
  items: ComplianceRecord[];
  has_more: boolean;
  next_cursor?: string;
};

export type SweepHealth = {
  applicationGroupId: string;
  lastAttemptAt: string;
  lastSuccessAt?: string;
  lastFailureAt?: string;
  lastFailureCode?: string;
  freshUntil: string;
  state: 'succeeded' | 'failed' | 'incomplete' | 'stale';
};

export type SweepHealthPage = {
  items: SweepHealth[];
  has_more: boolean;
  next_cursor?: string;
};

export type GroupSweepHealth = {
  value?: SweepHealth;
  eligibleEnvironmentCount?: number;
};

export type PolicyDiagnostic = {
  ruleId: string;
  message: string;
  reason: string;
  mode: 'audit' | 'deny';
  fieldPath?: string;
};

export type StackPolicyEvaluation = {
  id: string;
  application_group_id: string;
  environment: string;
  operation: string;
  phase: string;
  rollout: string;
  action: string;
  requiredAction: string;
  created_at: string;
  evaluation: {
    status: 'evaluated' | 'unavailable';
    decision: string;
    problem?: { code: string; message: string };
    result?: {
      coverage: { complete: boolean; unknownFields: string[] };
      violations: PolicyDiagnostic[];
      warnings: PolicyDiagnostic[];
      unresolved: PolicyDiagnostic[];
    };
  };
};

export type StackPolicyEvaluationPage = {
  items: StackPolicyEvaluation[];
  has_more: boolean;
  next_cursor?: string;
};

export interface PolicyApi {
  getPolicyBinding(
    tenant: string,
    ref: PolicyRef,
    signal?: AbortSignal,
  ): Promise<Record<string, unknown>>;
  getPolicyRevision(
    tenant: string,
    ref: PolicyRef,
    signal?: AbortSignal,
  ): Promise<Record<string, unknown>>;
  listAuthorizedTenants(signal?: AbortSignal): Promise<string[]>;
  listAdministrationTenants(
    signal?: AbortSignal,
  ): Promise<PolicyAdministrationTenants>;
  listTenantPolicies(
    tenant: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<PolicyLibraryPage>;
  listTenantCompliance(
    tenant: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<CompliancePage>;
  listPlatformPolicySummary(
    tenant: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<PlatformPolicySummaryPage>;
  listQuotas(
    tenant: string,
    applicationGroupId?: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<QuotaPage>;
  refreshGroupObservations(
    tenant: string,
    applicationGroupId: string,
    signal?: AbortSignal,
  ): Promise<QuotaRefreshRequest>;
  getEffectiveBindings(
    tenant: string,
    applicationGroupId: string,
    signal?: AbortSignal,
  ): Promise<EffectiveBinding[]>;
  listCompliance(
    tenant: string,
    applicationGroupId: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<CompliancePage>;
  getSweepHealth(
    tenant: string,
    applicationGroupId: string,
    signal?: AbortSignal,
  ): Promise<GroupSweepHealth>;
  listTenantSweepHealth(
    tenant: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<SweepHealthPage>;
  listStackEvaluations(
    tenant: string,
    environment: string,
    applicationGroupId: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<StackPolicyEvaluationPage>;
}

export const policyApiRef: ApiRef<PolicyApi> = createApiRef<PolicyApi>({
  id: 'plugin.policy.service',
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function validGroupId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value,
  );
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
}

function isPolicyRef(value: unknown): value is PolicyRef {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.revision === 'string'
  );
}

function isDecimal(value: unknown): value is string {
  return typeof value === 'string' && /^-?\d+$/.test(value);
}

function isQuantity(value: unknown): value is QuotaQuantity {
  return (
    isRecord(value) &&
    (value.state === 'known' ||
      value.state === 'unknown' ||
      value.state === 'notApplicable') &&
    (value.state !== 'known' || isDecimal(value.value)) &&
    (value.value === undefined || isDecimal(value.value)) &&
    (value.reason === undefined || typeof value.reason === 'string')
  );
}

function isQuotaBudget(
  value: unknown,
  tenant: string,
  groupId?: string,
): value is QuotaBudget {
  if (!isRecord(value) || !isRecord(value.scope)) return false;
  const scope = value.scope;
  return (
    typeof value.budgetId === 'string' &&
    isPolicyRef(value.policyRef) &&
    isPolicyRef(value.bindingRef) &&
    typeof value.quotaId === 'string' &&
    scope.tenant === tenant &&
    (scope.level === 'tenant' ||
      (scope.level === 'applicationGroup' &&
        typeof scope.applicationGroupId === 'string' &&
        (!groupId || scope.applicationGroupId === groupId))) &&
    (!groupId || scope.level === 'applicationGroup') &&
    isStringArray(value.kinds) &&
    isStringArray(value.stages) &&
    isStringArray(value.operations) &&
    (value.mode === 'audit' || value.mode === 'deny') &&
    typeof value.enforcementState === 'string' &&
    typeof value.dimension === 'string' &&
    typeof value.unit === 'string' &&
    isDecimal(value.limit) &&
    [value.allocated, value.reserved, value.remaining, value.overage].every(
      isQuantity,
    ) &&
    isRecord(value.inventory) &&
    typeof value.inventory.revision === 'string' &&
    typeof value.inventory.capturedAt === 'string' &&
    typeof value.inventory.freshUntil === 'string' &&
    typeof value.inventory.complete === 'boolean' &&
    isStringArray(value.inventory.unresolved) &&
    typeof value.ledgerRevision === 'string' &&
    typeof value.schemaRevision === 'string'
  );
}

function isBindingForGroup(
  value: unknown,
  tenant: string,
  applicationGroupId: string,
): value is EffectiveBinding {
  if (
    !isRecord(value) ||
    !isPolicyRef(value.ref) ||
    !isPolicyRef(value.policyRef) ||
    (value.mode !== 'audit' && value.mode !== 'deny') ||
    !isRecord(value.scope) ||
    !isStringArray(value.kinds) ||
    !isStringArray(value.stages) ||
    !isStringArray(value.operations)
  ) {
    return false;
  }

  const scope = value.scope;
  if (scope.level === 'platform') return true;
  if (scope.tenant !== tenant) return false;
  if (scope.level === 'tenant') return true;
  return (
    scope.level === 'applicationGroup' &&
    scope.applicationGroupId === applicationGroupId
  );
}

function isComplianceRecordForGroup(
  value: unknown,
  applicationGroupId?: string,
): value is ComplianceRecord {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.applicationGroupId === 'string' &&
    validGroupId(value.applicationGroupId) &&
    (!applicationGroupId || value.applicationGroupId === applicationGroupId) &&
    typeof value.environment === 'string' &&
    typeof value.status === 'string' &&
    typeof value.decision === 'string' &&
    typeof value.evaluatedAt === 'string' &&
    (value.observedAt === undefined || typeof value.observedAt === 'string') &&
    (value.freshUntil === undefined || typeof value.freshUntil === 'string') &&
    isRecord(value.coverage) &&
    typeof value.coverage.complete === 'boolean' &&
    isStringArray(value.coverage.unknownFields) &&
    Array.isArray(value.results) &&
    value.results.every(
      result =>
        isRecord(result) &&
        typeof result.ruleId === 'string' &&
        typeof result.outcome === 'string' &&
        typeof result.mode === 'string' &&
        (result.message === undefined || typeof result.message === 'string') &&
        (result.reason === undefined || typeof result.reason === 'string'),
    )
  );
}

function isPolicyLibraryEntry(
  value: unknown,
  tenant: string,
): value is PolicyLibraryEntry {
  if (
    !isRecord(value) ||
    !isPolicyRef(value.ref) ||
    (value.tenant !== undefined &&
      value.tenant !== '' &&
      value.tenant !== tenant) ||
    !isStringArray(value.targetKinds) ||
    // The API omits empty arrays (a quota-only policy has no rules).
    (value.rules !== undefined && !Array.isArray(value.rules)) ||
    (value.quotas !== undefined && !Array.isArray(value.quotas))
  )
    return false;
  if (value.scope === undefined) return true;
  return (
    isRecord(value.scope) &&
    ((value.scope.level === 'platform' && !value.tenant) ||
      (value.scope.tenant === tenant &&
        (value.scope.level === 'tenant' ||
          (value.scope.level === 'applicationGroup' &&
            typeof value.scope.applicationGroupId === 'string' &&
            validGroupId(value.scope.applicationGroupId)))))
  );
}

function isSweepHealthForGroup(
  value: unknown,
  applicationGroupId: string,
): value is SweepHealth {
  return (
    isRecord(value) &&
    value.applicationGroupId === applicationGroupId &&
    typeof value.lastAttemptAt === 'string' &&
    (value.lastSuccessAt === undefined ||
      typeof value.lastSuccessAt === 'string') &&
    (value.lastFailureAt === undefined ||
      typeof value.lastFailureAt === 'string') &&
    (value.lastFailureCode === undefined ||
      typeof value.lastFailureCode === 'string') &&
    typeof value.freshUntil === 'string' &&
    (value.state === 'succeeded' ||
      value.state === 'failed' ||
      value.state === 'incomplete' ||
      value.state === 'stale')
  );
}

function isPolicyDiagnostic(value: unknown): value is PolicyDiagnostic {
  return (
    isRecord(value) &&
    typeof value.ruleId === 'string' &&
    typeof value.message === 'string' &&
    typeof value.reason === 'string' &&
    (value.mode === 'audit' || value.mode === 'deny') &&
    (value.fieldPath === undefined || typeof value.fieldPath === 'string')
  );
}

function isStackEvaluationForScope(
  value: unknown,
  environment: string,
  applicationGroupId: string,
): value is StackPolicyEvaluation {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    value.environment !== environment ||
    value.application_group_id !== applicationGroupId ||
    typeof value.operation !== 'string' ||
    typeof value.phase !== 'string' ||
    typeof value.rollout !== 'string' ||
    typeof value.action !== 'string' ||
    typeof value.requiredAction !== 'string' ||
    typeof value.created_at !== 'string' ||
    !isRecord(value.evaluation) ||
    (value.evaluation.status !== 'evaluated' &&
      value.evaluation.status !== 'unavailable') ||
    typeof value.evaluation.decision !== 'string'
  ) {
    return false;
  }

  const evaluation = value.evaluation;
  if (evaluation.status === 'unavailable') {
    return (
      isRecord(evaluation.problem) &&
      typeof evaluation.problem.code === 'string' &&
      typeof evaluation.problem.message === 'string'
    );
  }
  if (!isRecord(evaluation.result)) return false;
  const result = evaluation.result;
  return (
    isRecord(result.coverage) &&
    typeof result.coverage.complete === 'boolean' &&
    isStringArray(result.coverage.unknownFields) &&
    [result.violations, result.warnings, result.unresolved].every(
      diagnostics =>
        Array.isArray(diagnostics) && diagnostics.every(isPolicyDiagnostic),
    )
  );
}

export class PolicyClient implements PolicyApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async get(
    path: string,
    tenant: string,
    parameters: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<unknown> {
    if (!tenant) {
      throw new Error('A tenant is required');
    }
    const proxy = await this.discoveryApi.getBaseUrl('proxy');
    const query = new URLSearchParams({ tenant, ...parameters });
    const response = await this.fetchApi.fetch(
      `${proxy}/platform-backend/api/v1${path}?${query.toString()}`,
      { signal },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json() as Promise<unknown>;
  }

  async getPolicyRevision(
    tenant: string,
    ref: PolicyRef,
    signal?: AbortSignal,
  ): Promise<Record<string, unknown>> {
    const identifier = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
    if (!identifier.test(ref.id) || !identifier.test(ref.revision)) {
      throw new Error('A valid policy reference is required');
    }
    const result = await this.get(
      `/policies/${encodeURIComponent(ref.id)}/revisions/${encodeURIComponent(
        ref.revision,
      )}`,
      tenant,
      {},
      signal,
    );
    if (
      !isRecord(result) ||
      !isPolicyRef(result.ref) ||
      result.ref.id !== ref.id ||
      result.ref.revision !== ref.revision ||
      (result.tenant !== undefined &&
        result.tenant !== '' &&
        result.tenant !== tenant)
    ) {
      throw new Error('Invalid policy revision response');
    }
    return result;
  }

  async getPolicyBinding(
    tenant: string,
    ref: PolicyRef,
    signal?: AbortSignal,
  ): Promise<Record<string, unknown>> {
    const identifier = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
    if (!identifier.test(ref.id) || !identifier.test(ref.revision)) {
      throw new Error('A valid binding reference is required');
    }
    const result = await this.get(
      `/policy-bindings/${encodeURIComponent(ref.id)}/`,
      tenant,
      {},
      signal,
    );
    if (
      !isRecord(result) ||
      !isPolicyRef(result.ref) ||
      result.ref.id !== ref.id ||
      result.ref.revision !== ref.revision ||
      (result.mode !== 'audit' && result.mode !== 'deny')
    ) {
      throw new Error('Invalid policy binding response');
    }
    return result;
  }

  async listAuthorizedTenants(signal?: AbortSignal): Promise<string[]> {
    const proxy = await this.discoveryApi.getBaseUrl('proxy');
    const response = await this.fetchApi.fetch(
      `${proxy}/platform-backend/api/v1/authorized-tenants`,
      { signal },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    const result = (await response.json()) as unknown;
    if (!isStringArray(result))
      throw new Error('Invalid authorized tenants response');
    return result;
  }

  async listAdministrationTenants(
    signal?: AbortSignal,
  ): Promise<PolicyAdministrationTenants> {
    const tenants = await this.listAuthorizedTenants(signal);
    if (tenants.length === 0) return { tenants: [], platformAdmin: false };
    // Authorized tenancy does not imply tenant-admin access. Check each scope:
    // a user can be an admin in the second tenant but only a viewer in the first.
    const getScope = async (tenant: string) => {
      const access = await this.get('/authorization/me', tenant, {}, signal);
      if (!isRecord(access) || typeof access.platformAdmin !== 'boolean') {
        throw new Error('Invalid policy administration access response');
      }
      return access;
    };
    const first = await getScope(tenants[0]);
    if (first.platformAdmin) return { tenants, platformAdmin: true };
    const scopes = [first];
    // Bound browser fan-out for users with many tenant memberships.
    for (let start = 1; start < tenants.length; start += 8) {
      const batch = await Promise.all(
        tenants.slice(start, start + 8).map(getScope),
      );
      if (batch.some(access => access.platformAdmin === true)) {
        return { tenants, platformAdmin: true };
      }
      scopes.push(...batch);
    }
    return {
      tenants: tenants.filter(
        (_, index) => scopes[index].tenantRole === 'admin',
      ),
      platformAdmin: false,
    };
  }

  async listTenantPolicies(
    tenant: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<PolicyLibraryPage> {
    const result = await this.get(
      '/policies/',
      tenant,
      { limit: '50', ...(cursor ? { cursor } : {}) },
      signal,
    );
    if (
      !isRecord(result) ||
      !Array.isArray(result.policies) ||
      typeof result.has_more !== 'boolean' ||
      (result.has_more &&
        (typeof result.next_cursor !== 'string' || !result.next_cursor)) ||
      !result.policies.every(policy => isPolicyLibraryEntry(policy, tenant))
    ) {
      throw new Error('Invalid tenant policy library response');
    }
    return {
      ...result,
      policies: result.policies.map(policy => ({
        ...policy,
        rules: policy.rules ?? [],
        quotas: policy.quotas ?? [],
      })),
    } as PolicyLibraryPage;
  }

  async listTenantCompliance(
    tenant: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<CompliancePage> {
    const result = await this.get(
      '/policies/compliance',
      tenant,
      { limit: '50', ...(cursor ? { cursor } : {}) },
      signal,
    );
    if (
      !isRecord(result) ||
      !Array.isArray(result.items) ||
      typeof result.has_more !== 'boolean' ||
      (result.has_more &&
        (typeof result.next_cursor !== 'string' || !result.next_cursor)) ||
      !result.items.every(item => isComplianceRecordForGroup(item))
    ) {
      throw new Error('Invalid tenant policy findings response');
    }
    return result as CompliancePage;
  }

  async listPlatformPolicySummary(
    tenant: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<PlatformPolicySummaryPage> {
    const result = await this.get(
      '/policies/platform-summary',
      tenant,
      { limit: '10', ...(cursor ? { cursor } : {}) },
      signal,
    );
    const counts = [
      'activeGroups',
      'freshSweptGroups',
      'currentEvaluations',
      'violatingResources',
      'unknownEvaluations',
    ] as const;
    if (
      !isRecord(result) ||
      !Array.isArray(result.tenants) ||
      typeof result.evaluatedAt !== 'string' ||
      typeof result.has_more !== 'boolean' ||
      (result.has_more &&
        (typeof result.next_cursor !== 'string' || !result.next_cursor)) ||
      !result.tenants.every(
        item =>
          isRecord(item) &&
          typeof item.tenant === 'string' &&
          item.tenant.length > 0 &&
          (item.state === 'observed' || item.state === 'unavailable') &&
          (item.latestEvaluationAt === undefined ||
            typeof item.latestEvaluationAt === 'string') &&
          counts.every(key =>
            item.state === 'observed'
              ? isDecimal(item[key])
              : item[key] === undefined,
          ),
      )
    ) {
      throw new Error('Invalid platform policy summary response');
    }
    return result as PlatformPolicySummaryPage;
  }

  async listQuotas(
    tenant: string,
    applicationGroupId?: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<QuotaPage> {
    if (applicationGroupId && !validGroupId(applicationGroupId)) {
      throw new Error('An ApplicationGroup UUID is required');
    }
    const result = await this.get(
      '/policies/quotas',
      tenant,
      {
        limit: '50',
        ...(applicationGroupId
          ? { application_group_id: applicationGroupId }
          : {}),
        ...(cursor ? { cursor } : {}),
      },
      signal,
    );
    if (
      !isRecord(result) ||
      !Array.isArray(result.items) ||
      typeof result.snapshotRevision !== 'string' ||
      typeof result.evaluatedAt !== 'string' ||
      typeof result.has_more !== 'boolean' ||
      (result.has_more && typeof result.next_cursor !== 'string') ||
      !result.items.every(
        item =>
          isQuotaBudget(item, tenant, applicationGroupId) ||
          (applicationGroupId &&
            isQuotaBudget(item, tenant) &&
            item.scope.level === 'tenant'),
      )
    ) {
      throw new Error('Invalid policy quota response');
    }
    // Older Orchestrator versions include tenant budgets in a group-filtered
    // response for tenant admins. The group page presents only group budgets;
    // tenant budgets have their own view. Keep rejecting any sibling-group item.
    if (applicationGroupId) {
      return {
        ...(result as QuotaPage),
        items: (result.items as QuotaBudget[]).filter(
          item => item.scope.level === 'applicationGroup',
        ),
      };
    }
    return result as QuotaPage;
  }

  async refreshGroupObservations(
    tenant: string,
    applicationGroupId: string,
    signal?: AbortSignal,
  ): Promise<QuotaRefreshRequest> {
    if (!tenant || !validGroupId(applicationGroupId)) {
      throw new Error('A tenant and ApplicationGroup UUID are required');
    }
    const proxy = await this.discoveryApi.getBaseUrl('proxy');
    const query = new URLSearchParams({
      tenant,
      application_group_id: applicationGroupId,
    });
    const response = await this.fetchApi.fetch(
      `${proxy}/platform-backend/api/v1/policies/quotas/refresh?${query.toString()}`,
      { method: 'POST', signal },
    );
    if (!response.ok) throw await ResponseError.fromResponse(response);
    const result: unknown = await response.json();
    if (
      !isRecord(result) ||
      typeof result.queued !== 'number' ||
      !Number.isInteger(result.queued) ||
      typeof result.maxPerRequest !== 'number' ||
      !Number.isInteger(result.maxPerRequest) ||
      typeof result.message !== 'string'
    ) {
      throw new Error('Invalid group observation refresh response');
    }
    return result as QuotaRefreshRequest;
  }

  async getEffectiveBindings(
    tenant: string,
    applicationGroupId: string,
    signal?: AbortSignal,
  ): Promise<EffectiveBinding[]> {
    if (!validGroupId(applicationGroupId)) {
      throw new Error('An ApplicationGroup UUID is required');
    }
    const result = await this.get(
      '/policies/effective',
      tenant,
      { application_group_id: applicationGroupId },
      signal,
    );
    if (
      !Array.isArray(result) ||
      !result.every(item => isBindingForGroup(item, tenant, applicationGroupId))
    ) {
      throw new Error('Invalid effective policy response');
    }
    return result as EffectiveBinding[];
  }

  async listCompliance(
    tenant: string,
    applicationGroupId: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<CompliancePage> {
    if (!validGroupId(applicationGroupId)) {
      throw new Error('An ApplicationGroup UUID is required');
    }
    const result = await this.get(
      '/policies/compliance',
      tenant,
      {
        application_group_id: applicationGroupId,
        limit: '50',
        ...(cursor ? { cursor } : {}),
      },
      signal,
    );
    if (
      !isRecord(result) ||
      !Array.isArray(result.items) ||
      typeof result.has_more !== 'boolean' ||
      (result.has_more && !result.next_cursor) ||
      !result.items.every(item =>
        isComplianceRecordForGroup(item, applicationGroupId),
      )
    ) {
      throw new Error('Invalid policy compliance response');
    }
    return result as CompliancePage;
  }

  async getSweepHealth(
    tenant: string,
    applicationGroupId: string,
    signal?: AbortSignal,
  ): Promise<GroupSweepHealth> {
    if (!validGroupId(applicationGroupId)) {
      throw new Error('An ApplicationGroup UUID is required');
    }
    const result = await this.get(
      '/policies/compliance/health',
      tenant,
      { application_group_id: applicationGroupId, limit: '1' },
      signal,
    );
    if (
      !isRecord(result) ||
      !Array.isArray(result.items) ||
      result.items.length > 1 ||
      result.has_more !== false ||
      (result.eligibleEnvironmentCount !== undefined &&
        (typeof result.eligibleEnvironmentCount !== 'number' ||
          !Number.isSafeInteger(result.eligibleEnvironmentCount) ||
          result.eligibleEnvironmentCount < 0)) ||
      !result.items.every(item =>
        isSweepHealthForGroup(item, applicationGroupId),
      )
    ) {
      throw new Error('Invalid policy sweep health response');
    }
    return {
      value: result.items[0] as SweepHealth | undefined,
      eligibleEnvironmentCount: result.eligibleEnvironmentCount as
        | number
        | undefined,
    };
  }

  async listTenantSweepHealth(
    tenant: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<SweepHealthPage> {
    const result = await this.get(
      '/policies/compliance/health',
      tenant,
      { limit: '50', ...(cursor ? { cursor } : {}) },
      signal,
    );
    if (
      !isRecord(result) ||
      !Array.isArray(result.items) ||
      typeof result.has_more !== 'boolean' ||
      (result.has_more && typeof result.next_cursor !== 'string') ||
      !result.items.every(
        item =>
          isRecord(item) &&
          typeof item.applicationGroupId === 'string' &&
          validGroupId(item.applicationGroupId) &&
          isSweepHealthForGroup(item, item.applicationGroupId),
      )
    ) {
      throw new Error('Invalid tenant sweep health response');
    }
    return result as SweepHealthPage;
  }

  async listStackEvaluations(
    tenant: string,
    environment: string,
    applicationGroupId: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<StackPolicyEvaluationPage> {
    if (!environment || !validGroupId(applicationGroupId)) {
      throw new Error('A Stack name and ApplicationGroup UUID are required');
    }
    const result = await this.get(
      `/environments/${encodeURIComponent(environment)}/policy-evaluations`,
      tenant,
      { limit: '50', ...(cursor ? { cursor } : {}) },
      signal,
    );
    if (
      !isRecord(result) ||
      !Array.isArray(result.items) ||
      typeof result.has_more !== 'boolean' ||
      (result.has_more && !result.next_cursor) ||
      !result.items.every(item =>
        isStackEvaluationForScope(item, environment, applicationGroupId),
      )
    ) {
      throw new Error('Invalid Stack policy evaluation response');
    }
    return result as StackPolicyEvaluationPage;
  }
}
